import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@repo/utils";

/**
 * Variants de badge.
 *
 * `secondary` était strictement identique à `default` (`bg-primary`) : tous les
 * badges neutres de l'app — « Archivé », « Terminée », « Non commencé », « IA » —
 * s'affichaient en violet primaire pleine puissance. Il est désormais neutre.
 *
 * Les variants d'état passent par les tokens sémantiques `--etat-*` : aucune
 * couleur littérale, pour que le rebrand soit une édition de `globals.css`.
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-pastille border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-etat-en-attente-surface text-etat-en-attente-contenu",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        success:
          "border-transparent bg-etat-fait-surface text-etat-fait-contenu",
        warning:
          "border-transparent bg-etat-en-cours-surface text-etat-en-cours-contenu",
        /** @deprecated N'encode aucun état métier. Appelants à trier — voir plan §0A.2. */
        info: "border-transparent bg-etat-info-surface text-etat-info-contenu",
        /** @deprecated N'encode aucun état métier. Appelants à trier — voir plan §0A.2. */
        purple:
          "border-transparent bg-accent text-primary hover:bg-accent/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  children?: React.ReactNode;
}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
